import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import BusinessCard from "../components/BusinessCard";
import { MapPin, Tag, ArrowRight, Globe } from "@phosphor-icons/react";
import { injectListSchema, removeJsonLd, slugify } from "../lib/seo";

const SITE = "https://moncommerce68.fr";

function setMeta(title, description, canonicalPath) {
  document.title = title;
  const existing = document.querySelector('meta[name="description"]');
  if (existing) existing.setAttribute("content", description);
  else {
    const m = document.createElement("meta");
    m.name = "description";
    m.content = description;
    document.head.appendChild(m);
  }
  // Canonical link
  if (canonicalPath) {
    const canonicalUrl = `${SITE}${canonicalPath}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);
  }
  // OG
  const ogUrl = canonicalPath ? `${SITE}${canonicalPath}` : undefined;
  const tags = {
    "og:title": title,
    "og:description": description,
    "og:type": "website",
    "og:site_name": "Mon Commerce 68",
    "og:locale": "fr_FR",
    ...(ogUrl ? { "og:url": ogUrl } : {}),
    "twitter:card": "summary_large_image",
    "twitter:title": title,
    "twitter:description": description,
  };
  for (const [k, v] of Object.entries(tags)) {
    const sel = k.startsWith("twitter") ? `meta[name="${k}"]` : `meta[property="${k}"]`;
    let el = document.querySelector(sel);
    if (!el) {
      el = document.createElement("meta");
      if (k.startsWith("twitter")) el.setAttribute("name", k);
      else el.setAttribute("property", k);
      document.head.appendChild(el);
    }
    el.setAttribute("content", v);
  }
}

export function CityPage({ canonicalPrefix = "/villes" }) {
  const { city } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/seo/city/${city}`).then((r) => {
      setData(r.data);
      const title = `${r.data.city} — Annuaire des entreprises | Mon Commerce 68`;
      const desc = `Découvrez ${r.data.total} commerces, artisans et prestataires à ${r.data.city} (Haut-Rhin). Horaires, coordonnées, avis — mis à jour quotidiennement.`;
      setMeta(title, desc, `${canonicalPrefix}/${city}`);
      injectListSchema({
        items: r.data.items,
        pageTitle: title,
        pageDescription: desc,
        crumbs: [
          { name: "Accueil", item: `${SITE}/` },
          { name: "Annuaire", item: `${SITE}/annuaire` },
          { name: r.data.city, item: `${SITE}${canonicalPrefix}/${city}` },
        ],
      });
    }).catch(() => setData(false));
    return () => { removeJsonLd("mc68-itemlist"); removeJsonLd("mc68-breadcrumb"); };
  }, [city, canonicalPrefix]);

  if (data === false) return <div className="p-20 text-center" data-testid="city-404">Ville inconnue</div>;
  if (!data) return <div className="p-20 text-center text-muted-foreground">Chargement…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid={`city-page-${city}`}>
      <div className="label-uppercase mb-3">Haut-Rhin · 68</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mb-3">
        Entreprises à {data.city}
      </h1>
      <p className="text-muted-foreground max-w-2xl mb-10">
        {data.total} professionnels référencés à {data.city}. Explorez les commerces, artisans et prestataires locaux — avec IA de recherche, contacts directs et avis clients.
      </p>

      {data.categories?.length > 0 && (
        <div className="mb-12">
          <div className="label-uppercase mb-4">Catégories populaires à {data.city}</div>
          <div className="flex flex-wrap gap-2">
            {data.categories.map((c) => (
              <Link key={c.category} to={`${canonicalPrefix}/${city}/${slug(c.category)}`} className="text-sm border border-border bg-card hover:border-primary px-3 py-1.5 rounded-sm transition-colors" data-testid={`city-cat-${slug(c.category)}`}>
                {c.category} <span className="text-muted-foreground">· {c.count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <div className="text-muted-foreground">Aucune entreprise pour le moment.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="city-grid">
          {data.items.map((b) => <BusinessCard key={b.id} b={b} />)}
        </div>
      )}
    </div>
  );
}

export function CategoryPage() {
  const { category } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/seo/category/${category}`).then((r) => {
      setData(r.data);
      const title = `${r.data.category} dans le Haut-Rhin (68) | Mon Commerce 68`;
      const desc = `Trouvez un ${r.data.category} proche de vous dans le 68 : ${r.data.total} professionnels référencés avec coordonnées, horaires et avis.`;
      setMeta(title, desc, `/categories/${category}`);
      injectListSchema({
        items: r.data.items,
        pageTitle: title,
        pageDescription: desc,
        crumbs: [
          { name: "Accueil", item: `${SITE}/` },
          { name: "Annuaire", item: `${SITE}/annuaire` },
          { name: r.data.category, item: `${SITE}/categories/${category}` },
        ],
      });
    }).catch(() => setData(false));
    return () => { removeJsonLd("mc68-itemlist"); removeJsonLd("mc68-breadcrumb"); };
  }, [category]);

  if (data === false) return <div className="p-20 text-center" data-testid="cat-404">Catégorie inconnue</div>;
  if (!data) return <div className="p-20 text-center text-muted-foreground">Chargement…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid={`category-page-${category}`}>
      <div className="label-uppercase mb-3">Haut-Rhin · 68</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mb-3">{data.category} dans le Haut-Rhin</h1>
      <p className="text-muted-foreground max-w-2xl mb-10">
        {data.total} professionnels {data.category.toLowerCase()} référencés dans le département 68. Trouvez celui près de chez vous.
      </p>

      {data.cities?.length > 0 && (
        <div className="mb-12">
          <div className="label-uppercase mb-4">Villes où trouver un {data.category.toLowerCase()}</div>
          <div className="flex flex-wrap gap-2">
            {data.cities.map((c) => (
              <Link key={c.city} to={`/annuaire/${slug(c.city)}/${category}`} className="text-sm border border-border bg-card hover:border-primary px-3 py-1.5 rounded-sm transition-colors" data-testid={`cat-city-${slug(c.city)}`}>
                {c.city} <span className="text-muted-foreground">· {c.count}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-grid">
        {data.items.map((b) => <BusinessCard key={b.id} b={b} />)}
      </div>
    </div>
  );
}

export function CityCategoryPage({ canonicalPrefix = "/villes" }) {
  const { city, category } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/seo/city/${city}/${category}`).then((r) => {
      setData(r.data);
      const title = `${r.data.category} à ${r.data.city} (68) — ${r.data.total} adresses | Mon Commerce 68`;
      const desc = `${r.data.total} ${r.data.category.toLowerCase()} à ${r.data.city} dans le Haut-Rhin. Adresses, horaires, téléphone, avis — contactez directement.`;
      setMeta(title, desc, `${canonicalPrefix}/${city}/${category}`);
      injectListSchema({
        items: r.data.items,
        pageTitle: title,
        pageDescription: desc,
        crumbs: [
          { name: "Accueil", item: `${SITE}/` },
          { name: "Annuaire", item: `${SITE}/annuaire` },
          { name: r.data.city, item: `${SITE}${canonicalPrefix}/${city}` },
          { name: r.data.category, item: `${SITE}${canonicalPrefix}/${city}/${category}` },
        ],
      });
    }).catch(() => setData(false));
    return () => { removeJsonLd("mc68-itemlist"); removeJsonLd("mc68-breadcrumb"); };
  }, [city, category, canonicalPrefix]);

  if (data === false) return <div className="p-20 text-center">Page inconnue</div>;
  if (!data) return <div className="p-20 text-center text-muted-foreground">Chargement…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid="city-cat-page">
      <div className="label-uppercase mb-3 flex gap-2 items-center"><MapPin size={12}/> {data.city}  <span>·</span> <Tag size={12}/> {data.category}</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mb-3">{data.category} à {data.city}</h1>
      <p className="text-muted-foreground max-w-2xl mb-10">{data.total} professionnels référencés à {data.city} pour la catégorie {data.category.toLowerCase()}.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.items.map((b) => <BusinessCard key={b.id} b={b} />)}
      </div>
    </div>
  );
}

export function BorderPage({ kind: kindProp }) {
  const params = useParams();
  const kind = kindProp || params.kind;
  const [data, setData] = useState(null);
  const title = kind === "suisse" ? "Frontière suisse" : "Frontière allemande";
  useEffect(() => {
    api.get(`/seo/border/${kind}`).then((r) => {
      setData(r.data);
      const flag = kind === "suisse" ? "Suisse" : "Deutschland";
      setMeta(
        `Commerces frontaliers ${flag} — Haut-Rhin | Mon Commerce 68`,
        `Annuaire des commerces locaux côté ${flag === "Suisse" ? "Bâle / Saint-Louis" : "Fribourg / Chalampé"} — professionnels français du 68 accessibles en quelques minutes depuis la frontière.`
      );
    }).catch(() => setData(false));
  }, [kind]);

  if (data === false) return <div className="p-20 text-center">Page inconnue</div>;
  if (!data) return <div className="p-20 text-center text-muted-foreground">Chargement…</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16" data-testid={`border-${kind}`}>
      <div className="label-uppercase mb-3 flex items-center gap-2"><Globe size={14} weight="duotone"/> {title}</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium mb-3">
        {kind === "suisse" ? "Commerces français côté frontière suisse" : "Commerces français côté frontière allemande"}
      </h1>
      <p className="text-muted-foreground max-w-2xl mb-10">
        {kind === "suisse"
          ? "Accessibles depuis Bâle, Saint-Louis, Huningue et alentours. Tarifs français, qualité locale, contacts directs."
          : "Accessibles depuis Fribourg, Neuenburg, Mulhouse. Rendez-vous rapides, artisans français, contacts directs."}
      </p>

      <div className="flex flex-wrap gap-2 mb-10">
        {data.cities.map((c) => (
          <Link key={c} to={`/villes/${slug(c)}`} className="text-sm border border-border bg-card hover:border-primary px-3 py-1.5 rounded-sm">
            {c}
          </Link>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.items.map((b) => <BusinessCard key={b.id} b={b} />)}
      </div>
    </div>
  );
}

// Simple client-side slug (matches python-slugify default behavior enough for our city names)
function slug(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
